import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';
import TrainingShell from '../../components/capacitacion/TrainingShell';
import { useTrainingUser } from '../../hooks/useTrainingUser';

export default function TrainingLanding() {
  const { user, isLoading, registrar } = useTrainingUser();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nombre: '',
    email: '',
    empresa: '',
    rol: 'operador' as
      | 'administrador'
      | 'operador'
      | 'supervisor'
      | 'encargado_inventario',
    telefono: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/capacitacion/modulos', { replace: true });
    }
  }, [isLoading, user, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await registrar({
        nombre: form.nombre.trim(),
        email: form.email.trim(),
        empresa: form.empresa.trim(),
        rol: form.rol,
        telefono: form.telefono.trim() || undefined,
      });
      navigate('/capacitacion/modulos');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Error al registrar');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <TrainingShell showHeader={false}>
      <div className="max-w-xl mx-auto mt-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-ngs-cyan/15 flex items-center justify-center">
            <GraduationCap className="h-6 w-6 text-ngs-cyan" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              <span className="text-ngs-cyan">SuMinuta</span>{' '}
              <span className="text-ngs-orange">Capacitación</span>
            </h1>
            <p className="text-sm text-gray-400">Regístrate para comenzar tu formación</p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-ngs-cyan/20 bg-ngs-surface p-6 space-y-4"
        >
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Nombre completo *
            </label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              required
              minLength={2}
              className="w-full rounded-md bg-ngs-bg border border-gray-700 focus:border-ngs-cyan text-white px-3 py-2 outline-none"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="w-full rounded-md bg-ngs-bg border border-gray-700 focus:border-ngs-cyan text-white px-3 py-2 outline-none"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Empresa *
            </label>
            <input
              type="text"
              value={form.empresa}
              onChange={(e) => setForm({ ...form, empresa: e.target.value })}
              required
              className="w-full rounded-md bg-ngs-bg border border-gray-700 focus:border-ngs-cyan text-white px-3 py-2 outline-none"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Rol *
            </label>
            <select
              value={form.rol}
              onChange={(e) => setForm({ ...form, rol: e.target.value as any })}
              className="w-full rounded-md bg-ngs-bg border border-gray-700 focus:border-ngs-cyan text-white px-3 py-2 outline-none"
            >
              <option value="administrador">Administrador</option>
              <option value="operador">Operador</option>
              <option value="supervisor">Supervisor</option>
              <option value="encargado_inventario">
                Encargado de Inventario
              </option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Los administradores deben aprobar todos los módulos.
            </p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Teléfono (opcional)
            </label>
            <input
              type="tel"
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              className="w-full rounded-md bg-ngs-bg border border-gray-700 focus:border-ngs-cyan text-white px-3 py-2 outline-none"
            />
          </div>

          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ngs-orange text-ngs-bg font-semibold py-2.5 hover:bg-ngs-orange/90 disabled:opacity-60"
          >
            {submitting ? 'Registrando...' : 'Comenzar capacitación'}
          </button>
        </form>
      </div>
    </TrainingShell>
  );
}
